import React, { useContext, useRef } from 'react'
import QRCode from 'react-qr-code'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import { GlobalContext } from '../../../App'

// The public applicant sign-up lives on the tenant subdomain, which is where
// site configuration is always opened from.
export const applicationLinkUrl = () =>
  `${window.location.origin}/register?accountType=applicant`

const QR_EXPORT_SIZE = 1024

const ApplicationLinkCard = ({ applicationsEnabled }) => {
  const { setToast } = useContext(GlobalContext)
  const qrRef = useRef(null)
  const url = applicationLinkUrl()

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setToast({ severity: 'success', message: 'Application link copied' })
    } catch {
      setToast({
        severity: 'error',
        message: 'Could not copy the link — select it and copy manually.',
      })
    }
  }

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg')
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const img = new Image()
    img.onload = () => {
      const padding = QR_EXPORT_SIZE / 16
      const canvas = document.createElement('canvas')
      canvas.width = QR_EXPORT_SIZE + padding * 2
      canvas.height = QR_EXPORT_SIZE + padding * 2
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, padding, padding, QR_EXPORT_SIZE, QR_EXPORT_SIZE)
      const link = document.createElement('a')
      link.download = 'application-link-qr.png'
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
        <Stack gap={1} flex={1} minWidth={0}>
          <Typography variant="title3">Your application link</Typography>
          <Typography variant="caption">
            Share this on your website, job adverts or social media. Applicants
            create an account, then complete the application form. Existing
            applicants can sign in from the same page to continue their
            application.
          </Typography>
          <TextField
            value={url}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
          />
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
            >
              Copy link
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadQR}
            >
              Download QR code
            </Button>
          </Stack>
          {!applicationsEnabled && (
            <Alert severity="warning">
              New applications are switched off, so anyone opening this link
              will see an <strong>Applications closed</strong> message.
            </Alert>
          )}
        </Stack>
        <Box
          ref={qrRef}
          sx={{
            p: 1,
            bgcolor: '#fff',
            borderRadius: 1,
            alignSelf: { xs: 'center', sm: 'flex-start' },
            lineHeight: 0,
          }}
        >
          <QRCode value={url} size={128} />
        </Box>
      </Stack>
    </Paper>
  )
}

export default ApplicationLinkCard
